import React from "react";
import {
  allOrdersSagaAction,
  contractFindAllSagaAction,
} from "../../../../redux/saga/my-account/saga-actions";
import {
  Avatar,
  AvatarSize,
  Loader,
  LoaderSizes,
  Tabs,
} from "@hid-galaxy-ui/galaxy-react";
import TabContentOrderHistory from "./tab-content-order-history";
import TabContentPartnerDetails from "./tab-content-partner-details";
import TabContentPartnerUsers from "./tab-content-partner-users";
import { useLocation } from "react-router";
import { useAppDispatch, useAppSelector } from "../../../../redux";
import useProductListPageRoute from "../../../../hooks/useProductListPageRoute";
import PartnerImage from "../../../../common/assets/images/partner-image.png";
import { getUserListSagaAction } from "../../../../redux/saga/user/saga-actions";
import { getExternalContractSagaAction } from "../../../../redux/saga/external-contract/saga-actions";
import TabContentContractStatus from "./tab-content-contract-status";
import { usePartnerSessionModuleDenied } from "../../../../hooks/usePartnerSessionPermissions";
import { PARTNER_PORTAL_PERMISSION_KEYS as PP_KEYS } from "../../../../utils/permissions/partnerPortalPermissionKeys";
import "./Settings.scss";

const ALL_SETTINGS_TABS = [
  { key: "partner-details", label: "Partner Details" },
  { key: "partner-users", label: "Partner Users" },
  { key: "order-history", label: "Order History" },
] as const;

const Settings = () => {
  const [tabSelectedKey, setTabSelectedKey] = React.useState<string>("");
  const [tabsContracts, setTabsContracts] = React.useState([]);
  const { fetchedContractsPartnerID, fetchedOrdersPartnerID, status } =
    useAppSelector((state) => state.my_account);
  const { collection, status: statusPLP } = useAppSelector(
    (state) => state.plp
  );
  const { partnerDetails, status: statusPD } = useAppSelector(
    (state) => state.partner_details
  );
  const { fetchedUserListPartnerID } = useAppSelector((state) => state.user);
  const { fetchedExternalContractsPartnerID } = useAppSelector(
    (state) => state.external_contract
  );
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { getRouteIndex, dispatchIt } = useProductListPageRoute();
  const shopifyNavigationHandle = "service-catalog";
  const subCollection = collection[getRouteIndex(shopifyNavigationHandle)];
  const sortedCollection = React.useMemo(() => subCollection, [collection]);
  const orderHistoryDenied = usePartnerSessionModuleDenied(
    PP_KEYS.PARTNER_ORDER_MANAGEMENT
  );

  const tabsSettings = React.useMemo(
    () =>
      ALL_SETTINGS_TABS.filter(
        (tab) => tab.key !== "order-history" || !orderHistoryDenied
      ),
    [orderHistoryDenied]
  );

  React.useEffect(() => {
    if (tabsSettings.length === 0) return;
    const allowed = tabsSettings.some((t) => t.key === tabSelectedKey);
    if (!allowed) {
      setTabSelectedKey(tabsSettings[0].key);
    }
  }, [tabsSettings, tabSelectedKey]);

  React.useEffect(() => {
    window.scrollTo({ top: 0 });
    if (location.state) {
      const element = document.getElementById("all-tabs-container");
      element?.scrollIntoView({ behavior: "smooth", block: "start" });
      if (
        location.state.selectedTab === "order-history" &&
        !orderHistoryDenied
      ) {
        setTabSelectedKey("order-history");
      }
    }
    if (collection[getRouteIndex(shopifyNavigationHandle)]?.length === 0) {
      dispatchIt(shopifyNavigationHandle);
    }
  }, []);

  React.useEffect(() => {
    let collectionTabs = [];
    if (sortedCollection.length > 0) {
      const dynamicTabs = sortedCollection.map((collection) => ({
        key: collection?.handle?.trim().toLowerCase().replaceAll("-", "_"),
        label: collection.title,
      }));
      collectionTabs.push(...dynamicTabs);
    }
    // collectionTabs.push({ key: "direct-contracts", label: "Direct Contracts" });
    setTabsContracts(collectionTabs);
  }, [collection]);

  React.useEffect(() => {
    if (partnerDetails) {
      if (fetchedUserListPartnerID !== partnerDetails.ID)
        dispatch(getUserListSagaAction(partnerDetails.ID));
      if (fetchedExternalContractsPartnerID !== partnerDetails.ID)
        dispatch(getExternalContractSagaAction(partnerDetails.ID));
      if (fetchedContractsPartnerID !== partnerDetails.ID)
        dispatch(contractFindAllSagaAction(partnerDetails.ID));
      if (fetchedOrdersPartnerID !== partnerDetails.ID)
        dispatch(allOrdersSagaAction(partnerDetails.ID));
    }
  }, [partnerDetails]);

  function onTabClickHandler(e: any, key: string): void {
    setTabSelectedKey(key);
  }

  return (
    <div className="partner-settings-container">
      <h1 className="page-title">Settings</h1>
      <div className="info-container">
        <div className="header-bar" />
        {statusPD.partnerDetailsLoading || !partnerDetails ? (
          <div className="loading">
            <Loader variant={LoaderSizes.large} />
          </div>
        ) : (
          <div className="details-wrapper">
            <div className="short-partner-details">
              <Avatar image={PartnerImage} size={AvatarSize.XXLarge} />
              <div className="name">{partnerDetails.partner_name}</div>
              <div className="type-label">Partner Type</div>
              <div className="types-container">
                {partnerDetails.partner_type.map((type) => (
                  <div className="box" key={type}>
                    {type}
                  </div>
                ))}
              </div>
            </div>
            <div className="custom-divider" />
            <div className="contract-status-tab">
              {statusPLP.loading || status.contractsLoading ? (
                <div className="loading-tabs">
                  <Loader variant={LoaderSizes.large} />
                </div>
              ) : tabsContracts.length > 0 ? (
                <Tabs
                  theme="frameless"
                  items={tabsContracts.map((tab) => {
                    return {
                      key: tab.key,
                      label: tab.label,
                      content: (
                        <TabContentContractStatus tabKey={tab.key} />
                      ),
                    };
                  })}
                  onClick={onTabClickHandler}
                />
              ) : (
                <></>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="all-tabs-container" id="all-tabs-container">
        {tabsSettings.length > 0 ? (
          <Tabs
            theme="frameless"
            selectedKey={tabSelectedKey}
            items={tabsSettings.map((tab) => {
              let content;
              switch (tab.key) {
                case "partner-details":
                  content = <TabContentPartnerDetails />;
                  break;
                case "partner-users":
                  content = <TabContentPartnerUsers />;
                  break;
                case "order-history":
                  content = <TabContentOrderHistory />;
                  break;
                default:
                  content = <div>Not Found</div>;
                  break;
              }
              return {
                key: tab.key,
                label: tab.label,
                content,
              };
            })}
            onClick={onTabClickHandler}
          />
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default Settings;
